import {ChangeDetectionStrategy, Component, forwardRef, input, InputSignal, output, OutputEmitterRef, signal, WritableSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {EMPTY, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {TextInputComponent} from '../text-input/text-input.component';
import {AppInputColorEnum} from '../../../enums/input/input-color.enum';
import {BasicSubscribableComponent} from '../../dummy-components/basic-subscribable-component';
import {STATIC_CONFIGS} from '../../../../configs/static.config';
import {AutocompleteSuggestion} from '../../../models/forms/autocomplete-suggestion.model';
import {IconComponent} from '../../icon/icon.component';
import {ApiVaultIconUrlPipe} from '../../../pipes/api-icon-url/api-vault-icon-url.pipe';
import {AuthImagePipe} from '../../../pipes/auth-image.pipe';
import {ICONS} from '../../../constants/icons/icons.constants';
import {ContentPrefixDirective} from '../../../directives/content-prefix.directive';
import {AppIconSizeEnum} from '../../../enums/app-icon-size.enum';
import {Icon} from '../../../types/icons/icon.type';

const DEBOUNCE_TIME_MS = 300;

@Component({
    selector: 'app-autocomplete-input',
    imports: [
        FormsModule,
        IconComponent,
        TextInputComponent,
        ApiVaultIconUrlPipe,
        AuthImagePipe,
        ContentPrefixDirective
    ],
    templateUrl: './autocomplete-input.component.html',
    styleUrl: './autocomplete-input.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[style.--max-visible-items]': 'maxVisibleItems()',
        '[class.color-default]': 'inputColor() === AppInputColorEnum.DEFAULT',
        '[class.color-login]': 'inputColor() === AppInputColorEnum.LOGIN',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteInputComponent),
            multi: true
        }
    ]
})
export class AutocompleteInputComponent extends BasicSubscribableComponent implements ControlValueAccessor {
    public hasClearButton: InputSignal<boolean> = input<boolean>(true);
    public id: InputSignal<string> = input.required<string>();
    public inputChange: OutputEmitterRef<string> = output<string>();
    public inputColor: InputSignal<AppInputColorEnum> = input<AppInputColorEnum>(AppInputColorEnum.DEFAULT);
    public isLoading: InputSignal<boolean> = input<boolean>(false);
    public maxInputLength: InputSignal<number | undefined> = input<number | undefined>(undefined);
    public maxVisibleItems: InputSignal<number> = input<number>(STATIC_CONFIGS.ui.input.suggestions.defaultCount);
    public minCharsToOpen: InputSignal<number> = input<number>(3);
    public minInputLength: InputSignal<number | undefined> = input<number | undefined>(undefined);
    public qaTag: InputSignal<string> = input<string>('autocomplete');
    public suggestions: InputSignal<Array<AutocompleteSuggestion>> = input<Array<AutocompleteSuggestion>>([]);
    public prefixIconUrl: InputSignal<string | undefined> = input<string>();
    public prefixIcon: InputSignal<Icon | undefined> = input<Icon>();
    public prefixIconFallback: InputSignal<Icon | undefined> = input<Icon>();
    public hasPrefixOnInputChange: InputSignal<boolean> = input<boolean>(false);
    protected readonly AppInputColorEnum = AppInputColorEnum;
    protected readonly activeIndex: WritableSignal<number> = signal(-1);
    protected readonly inputValue: WritableSignal<string> = signal('');
    protected readonly isDisabled: WritableSignal<boolean> = signal(false);
    protected readonly isOpen: WritableSignal<boolean> = signal(false);
    protected readonly ICONS = ICONS;
    protected readonly AppIconSize = AppIconSizeEnum;
    private readonly blur$: Subject<void> = new Subject<void>();
    private readonly inputValueChange$: Subject<string> = new Subject<string>();
    private onChange: (value: string) => void;
    private onTouched: () => void;

    public constructor() {
        super();

        this.onChange = (): void => {
        };
        this.onTouched = (): void => {
        };

        this.subscriptions.add(this.inputValueChange$.pipe(
                switchMap(value => (value.length >= this.minCharsToOpen() ? timer(DEBOUNCE_TIME_MS)
                    .pipe(takeUntil(this.blur$)) : EMPTY)),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                if (!this.isDisabled() && this.inputValue().length >= this.minCharsToOpen()) {
                    this.isOpen.set(true);
                    this.inputChange.emit(this.inputValue());
                }
            }));
    }

    public writeValue(value: string): void {
        this.inputValue.set(value ?? '');
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
        if (isDisabled) {
            this.isOpen.set(false);
        }
    }

    protected onTextInputChange(value: string): void {
        this.inputValue.set(value);
        this.activeIndex.set(-1);

        if (value.length < this.minCharsToOpen()) {
            this.isOpen.set(false);
        }

        this.onChange(value);
        this.inputValueChange$.next(value);
    }

    protected onInputFocus(): void {
        if (this.isDisabled()) {
            return;
        }

        if (this.inputValue().length === 0) {
            this.isOpen.set(true);
            this.inputChange.emit('');
        } else if (this.inputValue().length >= this.minCharsToOpen()) {
            this.isOpen.set(true);
            this.inputChange.emit(this.inputValue());
        }
    }

    protected onInputBlur(): void {
        this.blur$.next();
        this.onTouched();
        this.isOpen.set(false);
        this.activeIndex.set(-1);
    }

    protected onOptionPointerDown(event: PointerEvent): void {
        event.preventDefault();
    }

    protected onOptionPointerUp(_event: PointerEvent, option: string): void {
        this.selectOption(option);
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (!this.isOpen()) {
            return;
        }

        const suggestions = this.suggestions();
        const currentActiveIndex = this.activeIndex();

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (suggestions.length > 0) {
                    this.activeIndex.update(index => Math.min(index + 1, suggestions.length - 1));
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.activeIndex.update(index => Math.max(index - 1, -1));
                break;
            case 'Enter':
                if (currentActiveIndex >= 0 && currentActiveIndex < suggestions.length) {
                    event.preventDefault();
                    this.selectOption(suggestions[currentActiveIndex].text);
                }
                break;
            case 'Escape':
                event.preventDefault();
                this.isOpen.set(false);
                this.activeIndex.set(-1);
                break;
        }
    }

    private selectOption(option: string): void {
        this.inputValue.set(option);
        this.isOpen.set(false);
        this.activeIndex.set(-1);
        this.onChange(option);
        this.onTouched();
    }
}
